import { h, Host } from "@stencil/core";
import { dispatchEvent } from "./dispatch-event";
export class StaticFilter {
    constructor() {
        this.selectedValues = {};
        this.handleAnimation = false;
        this.filterOnLoad = true;
        this.maxItemsToShow = undefined;
        this.displayAllValues = undefined;
        this.nameAttributeReplacer = "data-ux-static-filter-name";
        this.hasFilteredClass = false;
        this.disableAnimation = false;
    }
    baseClass = "ux-static-filter";
    /** do not change the value of this attribute, as other components have a dependency to it */
    hiddenItemClass = "ux-static-filter__item--hidden";
    filteredItemClass = "ux-static-filter__item--filtered";
    itemsFilterAttribute = "data-ux-static-filter";
    element;
    /**
     * uxStaticFilter Event
     */
    staticFilterEvent;
    onChangeEvent = (e) => {
        const eTarget = e.target;
        const eTargetValue = eTarget.querySelector("input, select");
        if (this.filterOnLoad) {
            this.filterOnLoad = false;
        }
        const key = this.getElementNameValue(eTarget) || eTarget.name;
        let value = eTargetValue.value;
        if (e.type === "uxCheckboxChange") {
            value = eTargetValue.checked ? eTargetValue.value : "";
        }
        this.selectedValues = {
            ...this.selectedValues,
            [key]: value
        };
    };
    getElementNameValue = (element) => {
        return (element.closest("ux-select")?.getAttribute(this.nameAttributeReplacer) ||
            element.closest("ux-radio-button")?.getAttribute(this.nameAttributeReplacer) ||
            element.closest("ux-radio-list-item")?.getAttribute(this.nameAttributeReplacer) ||
            element.closest("ux-checkbox")?.getAttribute(this.nameAttributeReplacer) ||
            null);
    };
    componentDidLoad() {
        const radioButtonElements = Array.from(this.element.querySelectorAll("ux-radio-button input:checked"));
        const checkboxElements = Array.from(this.element.querySelectorAll("ux-checkbox input:checked"));
        const selectElements = Array.from(this.element.querySelectorAll("ux-select select"));
        const radioButtonsValues = radioButtonElements.reduce((acc, element) => {
            return {
                ...acc,
                [this.getElementNameValue(element) || element.name]: element.value
            };
        }, {});
        const checkboxValues = checkboxElements.reduce((acc, element) => {
            return {
                ...acc,
                [this.getElementNameValue(element) || element.name]: element.value
            };
        }, {});
        const selectValues = selectElements.reduce((acc, element) => {
            return {
                ...acc,
                [this.getElementNameValue(element) || element.name]: element.value
            };
        }, {});
        this.selectedValues = {
            ...radioButtonsValues,
            ...checkboxValues,
            ...selectValues
        };
    }
    filterItems = async () => {
        const itemsToFilter = this.element.querySelectorAll(`[${this.itemsFilterAttribute}]`);
        /** 0.2s timer before we show or display items */
        if (!this.filterOnLoad) {
            await new Promise((resolve) => setTimeout(resolve, 200));
        }
        let counter = 0;
        // @ts-expect-error Check for typing
        itemsToFilter.forEach((item) => {
            let showItem = false;
            // @ts-expect-error Check for typing
            const itemFilters = item
                .getAttribute(this.itemsFilterAttribute)
                .split(",")
                .map((value) => value.trim());
            const filters = typeof this.displayAllValues === "string"
                ? [...itemFilters, ...this.displayAllValues.split(",").map((value) => value.trim())]
                : [...itemFilters];
            filters.forEach((filter) => {
                const filterName = filter.split("=")[0];
                const filterValue = filter.split("=")[1];
                if (this.selectedValues[filterName] === filterValue) {
                    showItem = true;
                }
            });
            if (showItem && (this.maxItemsToShow === undefined || counter < this.maxItemsToShow)) {
                this.showItem(item);
                counter++;
            }
            else {
                this.hideItem(item);
            }
        });
    };
    showItem = (item) => {
        if (!this.hasFilteredClass && item.classList.contains(this.hiddenItemClass)) {
            item.classList.remove(this.hiddenItemClass);
        }
        else if (this.hasFilteredClass && item.classList.contains(this.filteredItemClass)) {
            item.classList.remove(this.filteredItemClass);
        }
        this.staticFilterEvent.emit({ hidden: false, htmlElement: item });
    };
    hideItem = (item) => {
        if (!this.hasFilteredClass && !item.classList.contains(this.hiddenItemClass)) {
            item.classList.add(this.hiddenItemClass);
        }
        else if (this.hasFilteredClass && !item.classList.contains(this.filteredItemClass)) {
            item.classList.add(this.filteredItemClass);
        }
        this.staticFilterEvent.emit({ hidden: true, htmlElement: item });
    };
    onSelectedValueChange() {
        this.handleAnimation = true;
    }
    async componentDidRender() {
        if (this.handleAnimation) {
            await this.filterItems();
            this.handleAnimation = false;
            dispatchEvent("resize");
        }
    }
    render() {
        return (h(Host, { key: 'fe83407f8862e5074524f67d000972e58ff573b0', onUxRadioListItemChange: this.onChangeEvent, onUxSelectChange: this.onChangeEvent, onUxRadioButtonChange: this.onChangeEvent, onUxCheckboxChange: this.onChangeEvent, class: {
                [this.baseClass]: true,
                [`${this.baseClass}--hidden-items`]: this.disableAnimation ? false : this.handleAnimation
            } }, h("slot", { key: '1dfb17f8da02ae7f11a12b58a75bee5d8c2aeb2e' })));
    }
    static get is() { return "ux-static-filter"; }
    static get originalStyleUrls() {
        return {
            "$": ["static-filter.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["static-filter.css"]
        };
    }
    static get properties() {
        return {
            "maxItemsToShow": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number | undefined",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the maximum number of items to show at any given time"
                },
                "attribute": "max-items-to-show",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "displayAllValues": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "comma separated list of string values, by which all elements should be displayed"
                },
                "attribute": "display-all-values",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "nameAttributeReplacer": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if present on the target ux-select, ux-radio-list-item or ux-radio-button\nit will replace the \"name\" attribute for setting the filter name"
                },
                "attribute": "name-attribute-replacer",
                "reflect": false,
                "defaultValue": "\"data-ux-static-filter-name\""
            },
            "hasFilteredClass": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "adds the --filtered instead of the --hidden class to 'not active' filtered elements"
                },
                "attribute": "has-filtered-class",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableAnimation": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "disable the show/hide animation when a filter is changed"
                },
                "attribute": "disable-animation",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "selectedValues": {},
            "handleAnimation": {},
            "filterOnLoad": {}
        };
    }
    static get events() {
        return [{
                "method": "staticFilterEvent",
                "name": "uxStaticFilter",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "uxStaticFilter Event"
                },
                "complexType": {
                    "original": "{\n        hidden: boolean;\n        htmlElement: HTMLElement;\n    }",
                    "resolved": "{ hidden: boolean; htmlElement: HTMLElement; }",
                    "references": {
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "selectedValues",
                "methodName": "onSelectedValueChange"
            }];
    }
}
//# sourceMappingURL=static-filter.js.map
